/**
 * Phase 6b — Executive Forecast History & Accuracy
 * Permission: EXEC_FORECAST_HISTORY_LIHAT (super_admin, direktur_*, auditor).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, TrendingUp, Camera, AlertCircle } from "lucide-react";

function fmtRp(x) {
  if (x === null || x === undefined) return "—";
  const sign = x < 0 ? "-" : "";
  return `${sign}Rp ${Math.round(Math.abs(x)).toLocaleString("id-ID")}`;
}

export default function ExecutiveForecastHistory() {
  const [items, setItems] = useState([]);
  const [signals, setSignals] = useState({});
  const [accuracy, setAccuracy] = useState([]);
  const [err, setErr] = useState(null);
  const [working, setWorking] = useState(false);

  const load = () => {
    setErr(null);
    api.get("/executive/forecast-history")
      .then(({ data }) => {
        setItems(data.items || []);
        setSignals(data.change_signals || {});
      })
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses riwayat forecast.");
        else setErr(formatApiError(e));
      });
    api.get("/executive/forecast-accuracy")
      .then(({ data }) => setAccuracy(data.items || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const snapshot = async () => {
    setWorking(true);
    try {
      await api.post("/executive/forecast-history/snapshot");
      toast.success("Snapshot forecast berhasil dibuat.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-forecast-history-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-forecast-history-no-access">
          {err}
        </div>
      </div>
    );
  }

  const metricLabel = (m) => m === "revenue" ? "Omzet" : m === "net_cashflow" ? "Net Cashflow" : m;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exec-forecast-history-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-sky-700" /> Riwayat & Akurasi Forecast
          </h1>
          <p className="text-xs md:text-sm text-slate-500">
            Snapshot forecast rolling-average + linear trend. Perbandingan vs aktual dengan MAE/MAPE deterministik.
            <span className="font-semibold"> Tidak ada confidence probability.</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={snapshot} disabled={working}
                 data-testid="forecast-snapshot-btn">
          <Camera size={14} className="mr-1" />Snapshot Sekarang
        </Button>
      </div>

      {/* Change signals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="forecast-signals">
        {["revenue", "net_cashflow"].map((m) => {
          const s = signals[m];
          return (
            <Card key={m}>
              <CardHeader className="pb-1"><CardTitle className="text-sm">{metricLabel(m)} — Perubahan Forecast</CardTitle></CardHeader>
              <CardContent>
                {!s ? (
                  <div className="text-xs text-slate-400 italic">Data historis belum cukup</div>
                ) : (
                  <div className="space-y-1" data-testid={`signal-${m}`}>
                    <div className="text-xs text-slate-500">Terbaru: <span className="font-semibold">{fmtRp(s.latest)}</span></div>
                    <div className="text-xs text-slate-500">Sebelumnya: <span className="font-semibold">{fmtRp(s.previous)}</span></div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className={
                        s.signal ? "bg-amber-100 text-amber-900 border-amber-200" :
                                    "bg-slate-100 text-slate-700 border-slate-200"}>
                        {s.signal && <AlertCircle size={10} className="mr-1" />}
                        Δ {s.delta_percent?.toFixed(1)}%
                      </Badge>
                      <span className="text-[11px] text-slate-500">
                        {s.signal ? "Signifikan (>10%)" : "Dalam batas normal"}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Accuracy */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Akurasi Forecast</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Sample Size</TableHead>
                <TableHead className="text-right">MAE</TableHead>
                <TableHead className="text-right">MAPE %</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accuracy.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-xs text-slate-400 italic py-6">
                  Belum dapat dievaluasi
                </TableCell></TableRow>
              )}
              {accuracy.map((a) => (
                <TableRow key={a.metric} data-testid={`acc-row-${a.metric}`}>
                  <TableCell>{metricLabel(a.metric)}</TableCell>
                  <TableCell className="text-right">{a.sample_size}</TableCell>
                  <TableCell className="text-right">{a.mae !== undefined ? fmtRp(a.mae) : "—"}</TableCell>
                  <TableCell className="text-right">{a.mape_percent !== undefined && a.mape_percent !== null ? `${a.mape_percent.toFixed(1)}%` : "—"}</TableCell>
                  <TableCell className="text-xs">{a.available ? "Tersedia" : (a.note || "Belum dapat dievaluasi")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* History table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Riwayat Snapshot</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dibuat</TableHead>
                <TableHead>Metric</TableHead>
                <TableHead>Untuk Periode</TableHead>
                <TableHead className="text-right">Forecast</TableHead>
                <TableHead className="text-right">Aktual</TableHead>
                <TableHead className="text-right">Abs Error</TableHead>
                <TableHead className="text-right">% Error</TableHead>
                <TableHead>Method</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-xs text-slate-400 italic py-6">
                  Data historis belum cukup
                </TableCell></TableRow>
              )}
              {items.map((r) => (
                <TableRow key={r.id} data-testid={`fh-row-${r.id}`}>
                  <TableCell className="text-xs">{new Date(r.generated_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-xs">{metricLabel(r.metric)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.forecast_for_period}</TableCell>
                  <TableCell className="text-right text-xs">{fmtRp(r.forecast_value)}</TableCell>
                  <TableCell className="text-right text-xs">
                    {r.actual_available ? fmtRp(r.actual_value) : (
                      <span className="text-slate-400 italic">Belum dapat dievaluasi</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs">{r.actual_available ? fmtRp(r.absolute_error) : "—"}</TableCell>
                  <TableCell className="text-right text-xs">{r.percentage_error !== null && r.percentage_error !== undefined ? `${r.percentage_error.toFixed(1)}%` : "—"}</TableCell>
                  <TableCell className="text-[11px] text-slate-500">{r.method}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
